import CloseIcon from "@mui/icons-material/Close";
import DoneIcon from "@mui/icons-material/Done";
import SearchIcon from "@mui/icons-material/Search";
import Avatar from "@mui/material/Avatar";
import CircularProgress from "@mui/material/CircularProgress";
import IconButton from "@mui/material/IconButton";
import InputAdornment from "@mui/material/InputAdornment";
import List from "@mui/material/List";
import ListItem from "@mui/material/ListItem";
import ListItemAvatar from "@mui/material/ListItemAvatar";
import ListItemButton from "@mui/material/ListItemButton";
import ListItemText from "@mui/material/ListItemText";
import Popover from "@mui/material/Popover";
import TextField from "@mui/material/TextField";
import { styled } from "@mui/material/styles";
import React, { useMemo } from "react";
import { useSession } from "users/__session/session-context";
import Guid from "_shared/guids/guid";
import IndividualAvatar from "marketplace/_views/avatars/individual-avatar";
import useEveryNetworkConnection from "users/networking/connections/_hooks/use-every-network-connection";
import UserInformation from "users/information/user-information";
import { skipToken } from "@reduxjs/toolkit/query";
import isNullOrUndefined from "_shared/_utilities/is-null-or-undefined";
import { useGetIndividualsByUserIdsQuery } from "marketplace/individuals/_api/individual-api";
import Individual from "marketplace/individuals/individual";
import useWholeFirmRoster from "work/work-agreements/teams/client-team/_views/use-whole-firm-roster";

const PopoverContainer = styled("div")(({ theme }) => ({
  minWidth: "22rem",
  padding: theme.spacing(2),
  textAlign: "center",
}));
const SearchField = styled(TextField)(() => ({
  width: "100%",
})) as typeof TextField;
const LoaderProgress = styled(CircularProgress)(({ theme }) => ({
  display: "inline-block",
  marginBottom: theme.spacing(4),
  marginTop: theme.spacing(4),
}));
const ListContainer = styled(List)(() => ({
  maxHeight: "20rem",
  overflowX: "hidden",
  overflowY: "auto",
}));
const SelectedIcon = styled(Avatar)(({ theme }) => ({
  backgroundColor: theme.palette.primary.main,
  color: theme.palette.primary.contrastText,
}));
const NoSigningAuthorityNote = styled("span")(() => ({
  display: "block",
}));
const OmissionNote = styled("p")(({ theme }) => ({
  ...theme.typography.caption,
  color: theme.palette.text.secondary,
  margin: theme.spacing(1, 0, 0),
  maxWidth: "22rem",
  textAlign: "left",
}));

type ClientSelectorProps = {
  clientUserId?: Guid;
  popoverAnchor?: HTMLElement;
  onClientUpdated?: (userInfo?: UserInformation) => void;
  onPopoverClose?: () => void;
};

export default function ClientSelector(props: Readonly<ClientSelectorProps>) {
  const { clientUserId, popoverAnchor, onClientUpdated, onPopoverClose } = props;

  const session = useSession();

  const [searchTerm, setSearchTerm] = React.useState<string>("");

  const viewingAsVendor = Boolean(session.context?.viewingAsVendor);
  // Interim (#5434, plan Task 9): this picker still searches in the browser, so it reads every page
  // of the network and of the firm.
  const networkConnectionQuery = useEveryNetworkConnection();
  const entityMemberQuery = useWholeFirmRoster(session.user?.legalEntityId, { skip: viewingAsVendor });
  const userIds = useMemo(() => {
    if (!networkConnectionQuery.data && !entityMemberQuery.data) {
      return new Set<string>();
    }
    const idsFromConnections = networkConnectionQuery.data
      ?.map((nc) => nc.id)
      .filter(id => !isNullOrUndefined(id)) ?? [];
    const idsFromEntityMembers = entityMemberQuery.data?.map((em) => em.userId) ?? [];
    return new Set([...idsFromConnections, ...idsFromEntityMembers]);
  }, [networkConnectionQuery.data, entityMemberQuery.data]);

  const individualsQuery = useGetIndividualsByUserIdsQuery(
    userIds.size ? { userIds: [...userIds] } : skipToken
  );
  // The list is only known once the directory has answered for the people asked about; until then
  // "None Found" would be a claim about people it has not looked up yet.
  const isLoading = networkConnectionQuery.isLoading || individualsQuery.isLoading;

  const usersInfo = useMemo(() => {
    if (!individualsQuery.data) return [];
    const individuals = Individual.fromJSONArray(individualsQuery.data) ?? [];
    return individuals.map(ind => ind.toUserInformation());
  }, [individualsQuery.data]);

  // Who was asked about and did not come back. The directory admits every network connection since
  // #5538, so a connection missing here is one it could not load; a colleague missing here is one
  // who has not published a marketplace profile to a caller without member access to their firm.
  // Either way the picker says so, rather than leaving the person silently absent.
  const omissions = useMemo(() => {
    if (!individualsQuery.data) return { connectionNames: [], colleagueCount: 0 };
    const listedUserIds = new Set(usersInfo.map((u) => u.id?.value.toLowerCase()));
    const connections = networkConnectionQuery.data ?? [];
    const connectionUserIds = new Set(connections.map((nc) => nc.id?.toLowerCase()));
    const connectionNames = connections
      .filter((nc) => nc.id && !listedUserIds.has(nc.id.toLowerCase()))
      .map((nc) => `${nc.firstName} ${nc.lastName}`.trim());
    const colleagueCount = (entityMemberQuery.data ?? []).filter((em) => {
      const userId = em.userId.toLowerCase();
      return !connectionUserIds.has(userId) && !listedUserIds.has(userId);
    }).length;
    return { connectionNames, colleagueCount };
  }, [individualsQuery.data, usersInfo, networkConnectionQuery.data, entityMemberQuery.data]);

  const selectedClient = useMemo(() => {
    if (usersInfo.length === 0 || !clientUserId) return undefined;
    return usersInfo.find((c) => c.id?.isEqualTo(clientUserId));
  }, [clientUserId, usersInfo]);

  const clientFilterResults = useMemo(() => {
    const term = searchTerm.toLowerCase();
    const matches = term === ""
      ? usersInfo
      : usersInfo.filter((r) =>
        r.name.fullName.toLowerCase().includes(term) ||
        r.email?.value.toLowerCase().includes(term)
      );
    if (!selectedClient) return matches;
    // The representative already on the proposal leads the list, so the current selection is visible
    // without scrolling a long roster to find the row carrying the checkmark. A search that excludes
    // the selection still excludes it -- filtering that quietly kept a non-matching row would invite
    // a misclick on the very row the search was meant to move away from. A shorter `rest` is what
    // says the selection survived the search, so no separate pass has to go looking for it.
    const rest = matches.filter((r) => !r.id?.isEqualTo(selectedClient.id));
    return rest.length === matches.length ? matches : [selectedClient, ...rest];
  }, [searchTerm, selectedClient, usersInfo]);

  function search(value: string) {
    setSearchTerm(value);

  }

  function saveChanges(client?: UserInformation) {
    onClientUpdated?.(client);
    onPopoverClose?.();
  }

  return (
    <Popover
      anchorEl={popoverAnchor}
      anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
      transformOrigin={{ vertical: "top", horizontal: "left" }}
      open={popoverAnchor !== undefined}
      onClose={() => onPopoverClose?.()}
    >
      <PopoverContainer>
        <SearchField
          placeholder="Client Representatives"
          slotProps={{
            input: {
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              ),
              endAdornment: (
                <>
                  {searchTerm.length > 0 && (
                    <IconButton
                      size="small"
                      onClick={() => {
                        setSearchTerm("");
                        search("");
                      }}
                    >
                      <CloseIcon />
                    </IconButton>
                  )}
                </>
              ),
            },
          }}
          value={searchTerm}
          onChange={(event) => search(event.target.value)}
        />
        {isLoading && <LoaderProgress size={50} disableShrink />}
        {!isLoading && (
          <ListContainer dense>
            {clientFilterResults.length < 1 && (
              <ListItem>
                <ListItemText
                  primary="None Found"
                  slotProps={{ primary: { sx: { textAlign: "center", padding: 1, typography: "body1" } } }}
                />
              </ListItem>
            )}
            {clientFilterResults.length > 0 &&
              clientFilterResults.map((client: UserInformation) => {
                const isSelected = selectedClient?.id?.isEqualTo(client.id);
                // The Client Representative signs the agreement, so the API rejects the whole
                // proposal — "Does not have signing authority for legal entity." — for anyone who
                // cannot sign for their own entity (issue #3786). Showing them greyed out with the
                // reason beats offering a choice that only fails on save.
                const canSign = client.hasSigningAuthority === true;

                return (
                  <ListItemButton
                    disableGutters
                    disabled={!canSign}
                    key={client.id?.value}
                    selected={isSelected}
                    onClick={() => {
                      if (!client.id || !client.legalEntityId) {
                        throw new Error("Client or legal Entity ID is missing");
                      }
                      saveChanges(client);
                    }}
                  >
                    <ListItemAvatar>
                      {isSelected ? (
                        <SelectedIcon>
                          <DoneIcon />
                        </SelectedIcon>
                      ) : (
                        <IndividualAvatar
                          individual={client.toIndividual()}
                        />
                      )}
                    </ListItemAvatar>
                    <ListItemText
                      primary={client.name?.fullName}
                      secondary={
                        <>
                          {[client.email?.value, client.companyName].filter(Boolean).join(" · ")}
                          {!canSign && (
                            <NoSigningAuthorityNote>No signing authority for their legal entity</NoSigningAuthorityNote>
                          )}
                        </>
                      }
                    />
                  </ListItemButton>
                );
              })}
          </ListContainer>
        )}
        {!isLoading && (
          <>
            {individualsQuery.isError && (
              <OmissionNote role="alert">Client representatives could not be loaded. Close this list and try again.</OmissionNote>
            )}
            {omissions.connectionNames.length > 0 && (
              <OmissionNote>
                Not listed: {omissions.connectionNames.join(", ")}. They are in your network, but their details could not
                be loaded.
              </OmissionNote>
            )}
            {omissions.colleagueCount > 0 && (
              <OmissionNote>
                {omissions.colleagueCount === 1
                  ? "1 colleague is not listed, because they have not published a marketplace profile."
                  : `${omissions.colleagueCount} colleagues are not listed, because they have not published a marketplace profile.`}
              </OmissionNote>
            )}
            <OmissionNote>
              {viewingAsVendor
                ? "Only your network connections are listed. To choose another client, connect with them first."
                : "Only your network connections and colleagues are listed."}
            </OmissionNote>
          </>
        )}
      </PopoverContainer>
    </Popover>
  );
}
